import {
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  ElementFormatType,
  ElementNode,
  LexicalNode,
} from 'lexical';
import { $isListNode, ListNode } from '@lexical/list';
import { $isLinkNode, LinkNode } from '@lexical/link';
import { $getNearestNodeOfType, $findMatchingParent } from '@lexical/utils';
import { $isImageNode } from './image-node';
import { $isMentionNode } from './mention-node';

/**
 * Which toolbar buttons read as pressed for the current selection. Every format
 * that applies is reported, so nested formats all show at once (a caret in a link
 * inside a bulleted list presses both) — a button's pressed state always predicts
 * what pressing it will do.
 */
export interface ToolbarState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  elementFormat: ElementFormatType;
  listType: 'bullet' | 'number' | null;
  inLink: boolean;
  imageSelected: boolean;
}

export const INITIAL_TOOLBAR_STATE: ToolbarState = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  elementFormat: 'left',
  listType: null,
  inLink: false,
  imageSelected: false,
};

/**
 * Shallow comparison over every field, so the toolbar can skip re-rendering on
 * the update-listener traffic that ordinary typing generates.
 */
export function isSameToolbarState(a: ToolbarState, b: ToolbarState): boolean {
  return (
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.underline === b.underline &&
    a.strikethrough === b.strikethrough &&
    a.elementFormat === b.elementFormat &&
    a.listType === b.listType &&
    a.inLink === b.inLink &&
    a.imageSelected === b.imageSelected
  );
}

/**
 * The link `node` lives in, if any, searching `node` itself first. Shared by the
 * Link button's pressed state and the modal that edits that link, so the two
 * can't disagree about which link the caret is in.
 *
 * Matches AutoLinkNode too — it extends LinkNode.
 */
export function $getEnclosingLinkNode(node: LexicalNode): LinkNode | null {
  const match = $findMatchingParent(node, $isLinkNode);
  return $isLinkNode(match) ? match : null;
}

function $getEnclosingListType(node: LexicalNode): 'bullet' | 'number' | null {
  const parent = node.getParent();
  const listNode = $isListNode(parent)
    ? parent
    : $getNearestNodeOfType(node, ListNode);
  if (!listNode) return null;
  return listNode.getListType() === 'number' ? 'number' : 'bullet';
}

function $getElementFormat(
  anchorNode: LexicalNode,
  parent: ElementNode | null
): ElementFormatType {
  let matchingParent;
  if ($isLinkNode(parent)) {
    matchingParent = $findMatchingParent(
      anchorNode,
      (parentNode) => $isElementNode(parentNode) && !parentNode.isInline()
    );
  }
  if ($isElementNode(matchingParent)) {
    return matchingParent.getFormatType();
  } else if ($isElementNode(anchorNode)) {
    return anchorNode.getFormatType();
  }
  return parent?.getFormatType() || 'left';
}

/**
 * Derives the toolbar's pressed state from the current selection. Must be called
 * inside `editor.read`/`editor.update` (or a command listener).
 *
 * `previous` is returned unchanged when there is no usable selection — that
 * happens while the link/image modal owns focus, and the toolbar underneath
 * shouldn't flicker off.
 */
export function $getToolbarState(previous: ToolbarState): ToolbarState {
  const selection = $getSelection();

  // A clicked image or mention is a NodeSelection, with no RangeSelection to
  // read text formats from, so reflect the format off the selected mention(s).
  // MentionFormatPlugin toggles these same formats when the buttons are pressed.
  if ($isNodeSelection(selection)) {
    const nodes = selection.getNodes();
    const mentions = nodes.filter($isMentionNode);
    const hasMentions = mentions.length > 0;
    const [firstNode] = nodes;

    return {
      ...previous,
      bold: hasMentions && mentions.every((m) => m.hasFormat('bold')),
      italic: hasMentions && mentions.every((m) => m.hasFormat('italic')),
      underline: hasMentions && mentions.every((m) => m.hasFormat('underline')),
      strikethrough:
        hasMentions && mentions.every((m) => m.hasFormat('strikethrough')),
      elementFormat: firstNode
        ? $getElementFormat(firstNode, firstNode.getParent())
        : previous.elementFormat,
      listType: firstNode ? $getEnclosingListType(firstNode) : null,
      // Left unpressed even when the selected node sits inside a link: the Link
      // button can only edit the link at a *range* selection (openLinkModal bails
      // on node selections and opens in "add" mode), so pressing it here would
      // promise an edit that won't happen.
      inLink: false,
      imageSelected: nodes.some($isImageNode),
    };
  }

  if (!$isRangeSelection(selection)) return previous;

  const anchorNode = selection.anchor.getNode();

  return {
    bold: selection.hasFormat('bold'),
    italic: selection.hasFormat('italic'),
    underline: selection.hasFormat('underline'),
    strikethrough: selection.hasFormat('strikethrough'),
    elementFormat: $getElementFormat(anchorNode, anchorNode.getParent()),
    listType: $getEnclosingListType(anchorNode),
    inLink: $getEnclosingLinkNode(anchorNode) !== null,
    // RANGE selections spanning images don't count as image-selected; only direct selection does:
    imageSelected: false,
  };
}
